import { Button } from "@components/buttons/button";
import { CardPaymentCheckout } from "@components/payment/card-checkout";
import { ICheckOutDTO } from "@components/payment/card-checkout/types";
import { IRaffleDefaultDTO } from "@hooks/dtos/raffles/IRaffleDefaultDTO";
import { Masks } from "@hooks/functions/Masks/Masks";
import { getRaffleStatus } from "@hooks/functions/Raffle/status";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  buildRaffleCheckoutSignature,
  clearRaffleCheckoutSession,
  getRaffleCheckoutSession,
  saveRaffleCheckoutSession,
} from "./checkoutStorage";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg: "linear-gradient(160deg, #fff 0%, #ECF4FF 60%, #D6E9FF 100%)",
  cardBg: "#ffffff",
  border: "#e4e8f0",
  headerBg: "#f8f9fc",
  text: "#111827",
  textMuted: "#6b7280",
  textLight: "#9ca3af",
  // Blue accent: always blue-700 (#1d4ed8) on blue-50 (#eff4ff) — WCAG AA
  accentBg: "#eff4ff",
  accentBorder: "#bfdbfe",
  accentText: "#1d4ed8",
  // Button: white text on blue-600 — high contrast
  btnBg: "#2563eb",
  btnDisabled: "#93c5fd",
  btnText: "#ffffff",
  // Total row: white on navy — unambiguous contrast
  totalBg: "#1e3a8a",
  totalText: "#ffffff",
  // Success: green-700 (#15803d) on green-50 (#f0fdf4)
  successBg: "#f0fdf4",
  successBorder: "#bbf7d0",
  successText: "#15803d",
  // Danger: red-700 (#b91c1c) on transparent
  dangerText: "#b91c1c",
  // Warning: amber-800 (#92400e) on amber-50 (#fffbeb)
  warnBg: "#fffbeb",
  warnBorder: "#fcd34d",
  warnText: "#92400e",
};

// Shared card shell
const cardStyle: React.CSSProperties = {
  background: C.cardBg,
  border: `1px solid ${C.border}`,
  borderRadius: 12,
  overflow: "hidden",
};

// Shared card header row
const cardHeaderStyle: React.CSSProperties = {
  background: C.headerBg,
  borderBottom: `1px solid ${C.border}`,
  padding: "13px 20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minHeight: 48,
};

// Uppercase muted label used in every card header
const cardHeaderLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: C.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  margin: 0,
};

// ─── Component ────────────────────────────────────────────────────────────────
const masks = new Masks();
export function RaffleTicketCheckout() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const routeState = state || {};
  const hasRouteContext = Boolean(
    routeState.raffle ||
    routeState.raffleKey ||
    routeState.campaign ||
    routeState.selectedTickets,
  );
  const routeSignature = buildRaffleCheckoutSignature(
    routeState.raffleKey || routeState.campaign,
    routeState.selectedTickets,
  );
  const storedCheckoutSession = useMemo(
    () =>
      getRaffleCheckoutSession(hasRouteContext ? routeSignature : undefined),
    [hasRouteContext, routeSignature],
  );
  const _raffle = routeState.raffle || storedCheckoutSession?.raffle;
  const raffleKey = routeState.raffleKey || storedCheckoutSession?.raffleKey;
  const campaign = routeState.campaign || storedCheckoutSession?.campaign;
  const selectedTickets = hasRouteContext
    ? routeState.selectedTickets || []
    : storedCheckoutSession?.selectedTickets;
  const _checkout = routeState.checkout || storedCheckoutSession?.checkout;

  const [raffle] = useState<IRaffleDefaultDTO>(_raffle);
  const [tickets, setTickets] = useState<string[]>(selectedTickets ?? []);
  const [checkout, setCheckout] = useState<ICheckOutDTO | undefined>(_checkout);

  const unitPrice = raffle?.price ?? 0;
  const totalPrice = parseFloat((unitPrice * tickets.length).toFixed(2));
  const checkoutSignature = buildRaffleCheckoutSignature(
    raffleKey || campaign,
    tickets,
  );

  const handleCheckoutChange = useCallback(
    (checkoutData?: ICheckOutDTO) => {
      setCheckout(checkoutData);
      saveRaffleCheckoutSession({
        raffle,
        raffleKey,
        campaign,
        selectedTickets: tickets,
        checkout: checkoutData,
      });
    },
    [campaign, raffle, raffleKey, tickets],
  );
  const handleCheckoutPaid = useCallback(() => {
    if (!checkoutSignature) return;
    clearRaffleCheckoutSession(checkoutSignature);
  }, [checkoutSignature]);

  // Remove a single ticket from the selection
  const handleRemoveTicket = (ticket: string) => {
    if (checkout?.trId) return;
    setTickets((prev) => prev.filter((t) => t !== ticket));
  };

  const backToCampaign = () => {
    navigate(
      `/campaign/default?campaign=${campaign}${
        raffleKey ? `&key=${raffleKey}` : ""
      }`,
      {
        state: {
          raffle,
          raffleKey,
          campaign,
          selectedTickets: tickets,
        },
      },
    );
  };

  useEffect(() => {
    saveRaffleCheckoutSession({
      raffle,
      raffleKey,
      campaign,
      selectedTickets: tickets,
      checkout,
    });
  }, [campaign, checkout, raffle, raffleKey, tickets]);

  // ── Empty state ───────────────────────────────────────────────────────────
  if (!raffle) {
    return (
      <section
        style={{ background: C.bg, minHeight: "100vh", padding: "48px 16px" }}
      >
        <div className="container" style={{ maxWidth: 900 }}>
          <div
            style={{
              background: C.warnBg,
              border: `1px solid ${C.warnBorder}`,
              borderRadius: 10,
              padding: "13px 18px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: C.warnText,
              fontSize: 14,
            }}
          >
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>
              Não foi possível recuperar os dados do checkout. Escolha uma
              campanha para continuar.
            </span>
          </div>

          <div className="d-flex col-md-6 mx-auto mt-3">
            <Button
              className="btn btn-warning text-white w-100 mt-2"
              onClick={() => navigate("/campaign/default/list")}
            >
              Ver campanhas
            </Button>
          </div>
        </div>
      </section>
    );
  }

  if (tickets.length === 0 && !checkout?.trId) {
    return (
      <section
        style={{ background: C.bg, minHeight: "100vh", padding: "48px 16px" }}
      >
        <div className="container" style={{ maxWidth: 900 }}>
          <div
            style={{
              background: C.warnBg,
              border: `1px solid ${C.warnBorder}`,
              borderRadius: 10,
              padding: "13px 18px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: C.warnText, // amber-800 on amber-50 — readable
              fontSize: 14,
            }}
          >
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>Nenhum ticket selecionado. Volte e escolha-os.</span>
          </div>

          <div className="d-flex col-md-6 mx-auto mt-3" style={{}}>
            {/* Back Button */}
            <Button
              className={`btn btn-${`warning`} text-white w-100 mt-2`}
              onClick={backToCampaign}
            >
              {`Voltar para a Campanha`}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  const raffleStatus = getRaffleStatus(raffle?.status);
  return (
    <section
      style={{
        background: C.bg,
        minHeight: "100vh",
        padding: "48px 16px 72px",
      }}
    >
      <div className="container" style={{ maxWidth: 900 }}>
        {/* Page header */}
        <div style={{ marginBottom: 32 }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: C.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              margin: "0 0 6px",
            }}
          >
            Finalizar compra
          </p>
          <h1
            style={{ fontSize: 24, fontWeight: 700, color: C.text, margin: 0 }}
          >
            Resumo do Pedido
          </h1>
        </div>

        <div className="row g-4 align-items-start">
          {/* ── Left column ───────────────────────────────────────────────── */}
          <div className="col-12 col-lg-7 d-flex flex-column gap-3">
            {/* Campaign card */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <span style={cardHeaderLabel}>Campanha</span>
                {/* green-700 on green-50: always readable */}
                <span
                  className={`badge bg-${raffleStatus?.color} text-center py-2 px-3`}
                  style={{
                    fontSize: "0.8rem",
                    fontWeight: "600",
                    letterSpacing: "0.3px",
                    borderRadius: "6px",
                  }}
                  title={raffleStatus?.title}
                >
                  {raffleStatus?.text}
                </span>
              </div>
              <div
                style={{
                  padding: "18px 20px",
                  display: "flex",
                  gap: 16,
                  alignItems: "center",
                }}
              >
                {raffle.thumb && (
                  <img
                    src={raffle.thumb}
                    alt={raffle.name}
                    style={{
                      width: 64,
                      height: 64,
                      objectFit: "cover",
                      borderRadius: 8,
                      flexShrink: 0,
                      border: `1px solid ${C.border}`,
                    }}
                  />
                )}
                <div style={{ minWidth: 0 }}>
                  <p
                    style={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: C.text,
                      margin: "0 0 4px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {raffle.name}
                  </p>
                  <p
                    style={{
                      fontSize: 13,
                      color: C.textMuted,
                      margin: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {raffle.description_short}
                  </p>
                </div>
              </div>
            </div>

            {/* Tickets list card */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <span style={cardHeaderLabel}>
                  Ticket{tickets.length > 0 ? "s" : ""} Selecionado
                  {tickets.length > 0 ? "s" : ""}
                </span>
                {/* blue-700 (#1d4ed8) on blue-50 (#eff4ff) — WCAG AA compliant */}
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    background: C.accentBg,
                    color: C.accentText,
                    border: `1px solid ${C.accentBorder}`,
                    borderRadius: 20,
                    padding: "3px 12px",
                  }}
                >
                  {tickets.length} {tickets.length === 1 ? "ticket" : "tickets"}
                </span>
              </div>

              <div style={{ maxHeight: 300, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: C.headerBg }}>
                      {["Ticket", "Valor Unit.", ""].map((h, i) => (
                        <th
                          key={i}
                          style={{
                            padding: "10px 20px",
                            textAlign: i === 1 ? "right" : "left",
                            fontSize: 11,
                            fontWeight: 700,
                            color: C.textMuted,
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            borderBottom: `1px solid ${C.border}`,
                            width: i === 2 ? 52 : "auto",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((ticket, idx) => (
                      <tr
                        key={ticket}
                        style={{
                          background: idx % 2 === 0 ? C.cardBg : C.headerBg,
                          borderBottom: `1px solid ${C.border}`,
                        }}
                      >
                        <td
                          style={{
                            padding: "12px 20px",
                            verticalAlign: "middle",
                          }}
                        >
                          {/* Ticket badge — blue-700 on blue-50 */}
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              fontFamily: "monospace",
                              background: C.accentBg,
                              color: C.accentText,
                              border: `1px solid ${C.accentBorder}`,
                              borderRadius: 6,
                              padding: "3px 10px",
                            }}
                          >
                            {ticket}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "12px 20px",
                            textAlign: "right",
                            verticalAlign: "middle",
                            fontSize: 14,
                            fontWeight: 600,
                            color: C.text,
                          }}
                        >
                          {masks.brlCurrency(unitPrice)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          {/* Remove button — locked after payment is generated */}
                          <button
                            onClick={() => handleRemoveTicket(ticket)}
                            disabled={!!checkout?.trId}
                            title="Remover ticket"
                            style={{
                              background: "none",
                              border: "none",
                              padding: "5px 7px",
                              borderRadius: 6,
                              cursor: checkout?.trId
                                ? "not-allowed"
                                : "pointer",
                              color: checkout?.trId
                                ? C.textLight
                                : C.dangerText,
                              opacity: checkout?.trId ? 0.4 : 1,
                              lineHeight: 1,
                            }}
                          >
                            <i
                              className="bi bi-trash3"
                              style={{ fontSize: 14 }}
                            ></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Back Button to add more tickets */}
              <Button
                className={`btn btn-primary text-white w-100 ${checkout?.url ? `disabled` : ``}`}
                onClick={backToCampaign}
              >
                {`Adicionar Tickets`}
              </Button>
            </div>

            {/* Price breakdown card */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <span style={cardHeaderLabel}>Extrato de Valores</span>
              </div>
              <div style={{ padding: "4px 0 0" }}>
                {[
                  {
                    label: "Preço unitário",
                    value: masks.brlCurrency(unitPrice),
                  },
                  {
                    label: "Quantidade",
                    value: `${tickets.length}`,
                  },
                  {
                    label: "Desconto",
                    value: masks.brlCurrency(0),
                  },
                ].map(({ label, value }) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "13px 20px",
                      borderBottom: `1px solid ${C.border}`,
                    }}
                  >
                    <span style={{ fontSize: 14, color: C.textMuted }}>
                      {label}
                    </span>
                    <span
                      style={{ fontSize: 14, fontWeight: 500, color: C.text }}
                    >
                      {value}
                    </span>
                  </div>
                ))}

                {/* Total — white (#fff) on navy (#1e3a8a): ratio ~10:1 */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: C.totalBg,
                    padding: "16px 20px",
                  }}
                >
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: C.totalText,
                    }}
                  >
                    Total a pagar
                  </span>
                  <span
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: C.totalText,
                    }}
                  >
                    {masks.brlCurrency(totalPrice)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Right column: payment ──────────────────────────────────────── */}
          <div className="col-12 col-lg-5">
            <CardPaymentCheckout
              raffleKey={raffleKey}
              totalPrice={totalPrice}
              tickets={tickets}
              checkout={checkout}
              onCheckoutChange={handleCheckoutChange}
              onCheckoutPaid={handleCheckoutPaid}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
